import { SEMANTIC, STATUS_SEMANTIC, tonal, tonalDeStatus } from './semantic';
import { deriveBrand } from './brand';

/**
 * Expande um theme.json MÍNIMO (só marca) para o formato completo que o app já
 * consome hoje.
 * ---------------------------------------------------------------------------
 * Esta é a peça que torna a migração não-destrutiva. `buildThemePalette`
 * (plugins/vuetify.js) lê 75 caminhos, `operation-status-colors` lê o bloco
 * workflowStatus e `dashboard-theme-utils` lê dashboardThemes. Nenhum deles
 * precisa mudar: o expand entrega a mesma forma, só que DERIVADA em vez de
 * escrita à mão em 8 arquivos.
 *
 * Assim os tenants migram um a um. O loader detecta o formato pela presença da
 * chave `brand`; quando os 8 estiverem migrados, o ramo legado sai.
 */

/** Grupos de status por domínio, como o app os organiza. */
const STATUS_POR_DOMINIO = {
  customer: [
    'cadastroEnviado',
    'cadastroEmAndamento',
    'cadastroFinalizado',
    'cadastroEmAnalise',
    'pendenciaDocumental',
    'enviadoAoBanco',
    'pldCompliance',
    'cadastroAprovado',
    'cadastroRecusado',
    'atualizacaoCadastral',
    'encerrado',
    'pendenciaBanco'
  ],
  exchangeBank: [
    'aguardandoAssinatura',
    'enviadoAoBanco',
    'pendenciaBanco',
    'cadastroPreAprovado',
    'cadastroAprovado',
    'cadastroRecusado',
    'encerrado'
  ],
  operation: [
    'emAnalise',
    'comPendencias',
    'analisePendencias',
    'emLiquidacao',
    'liquidada',
    'cancelada',
    'aguardando'
  ]
};

const grupoTonal = (chaves, variante) =>
  chaves.reduce((acc, k) => {
    acc[k] = { tonal: tonalDeStatus(k, variante) };

    return acc;
  }, {});

/** Rampa sequencial da marca → ink, para os passos da timeline. */
function rampa(de, para, passos) {
  const hex = (h) => {
    const n = parseInt(h.replace('#', ''), 16);

    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  };
  const a = hex(de);
  const b = hex(para);

  return Array.from({ length: passos }, (_, i) => {
    const k = passos === 1 ? 0 : i / (passos - 1);
    const c = {
      r: a.r + (b.r - a.r) * k,
      g: a.g + (b.g - a.g) * k,
      b: a.b + (b.b - a.b) * k
    };

    return (
      '#' +
      [c.r, c.g, c.b].map((x) => Math.round(x).toString(16).padStart(2, '0')).join('').toUpperCase()
    );
  });
}

/**
 * @param {object} marca  bloco `brand` do theme.json novo
 * @param {'light'|'dark'} variante
 * @returns {object} seção compatível com o formato legado
 */
export function expandirSecao(marca, variante) {
  const P = SEMANTIC[variante];
  const primary = variante === 'dark' ? marca.primaryDark || marca.primary : marca.primary;
  const derivada = deriveBrand(primary, marca.primary, { darkPrimary: marca.primaryDark });
  // Sem acento declarado, o padrão é o derivado da marca — nunca o shell, que é
  // o próprio fundo do menu e daria um acento invisível sobre ele.
  const acento = marca.accent || derivada.shellAccent;

  const passos = rampa(variante === 'dark' ? '#3D5A86' : P.bg === '#FAF9F6' ? '#0C2340' : P.bg, acento, 8);

  return {
    assets: {
      logo: marca.logo?.[variante],
      symbol: marca.symbol?.[variante],
      loginBackground: marca.loginBackground
    },

    primary,
    secondary: derivada.shell,
    tertiary: P.text,
    accent: acento,

    error: P.danger.solid,
    info: P.info.solid,
    success: P.success.solid,
    warning: P.warning.strong || P.warning.solid,
    warningText: P.warning.solid,

    background: { primary: P.bg, secondary: P.surface },

    workflowStatus: {
      customer: grupoTonal(STATUS_POR_DOMINIO.customer, variante),
      exchangeBank: grupoTonal(STATUS_POR_DOMINIO.exchangeBank, variante),
      operation: grupoTonal(STATUS_POR_DOMINIO.operation, variante)
    },

    operation: { color: { sell: { tonal: tonal('danger', variante) }, buy: { tonal: tonal('success', variante) } } },

    bankType: {
      standard: { tonal: tonalDeStatus('standard', variante) },
      signature: { tonal: tonalDeStatus('signature', variante) },
      automatic: { tonal: tonalDeStatus('automatic', variante) }
    },

    correspondent: {
      type: {
        indicator: { tonal: tonalDeStatus('indicator', variante) },
        corbanMaster: { tonal: tonalDeStatus('corbanMaster', variante) }
      },
      fallback: {
        indicator: { tonal: tonalDeStatus('indicator', variante) },
        corbanMaster: { tonal: tonalDeStatus('corbanMaster', variante) }
      }
    },

    status: {
      active: { tonal: tonalDeStatus('active', variante) },
      inactive: { tonal: tonalDeStatus('inactive', variante) }
    },

    table: { collapsiblePanel: P.surfaceRaised, card: P.surface, emptyState: P.textFaint },

    audit: { remove: P.danger.soft, add: P.success.soft },

    divider: { panel: P.line },

    notifications: {
      success: { border: P.success.solid, background: P.success.soft, text: P.success.solid },
      information: { border: P.info.solid, background: P.info.soft, text: P.info.solid },
      warning: { border: P.warning.solid, background: P.warning.soft, text: P.warning.solid },
      error: { border: P.danger.solid, background: P.danger.soft, text: P.danger.solid }
    },

    documentUpload: {
      area: {
        border: P.line,
        background: P.bg,
        backgroundHover: P.surfaceRaised,
        backgroundDragOver: P.info.soft,
        backgroundHasFile: P.success.soft,
        borderDragOver: P.info.solid,
        borderHasFile: P.success.solid,
        text: P.text,
        textSecondary: P.textMuted
      },
      existingDocument: {
        area: { border: P.line, background: P.surface },
        content: { background: P.surfaceRaised },
        iconWrapper: { background: P.info.soft },
        name: { text: P.text },
        status: { text: P.textMuted }
      }
    },

    timeline: {
      phaseSketch: P.textMuted,
      phasePayin: P.info.solid,
      phasePayout: derivada.action,
      phaseOperation: acento,
      phaseCommission: P.success.solid,
      ...passos.reduce((acc, cor, i) => ({ ...acc, [`step${i + 1}`]: cor }), {})
    },

    // Paletas de gráfico continuam explícitas: são escolha do usuário no
    // dashboard, não decorrência da identidade.
    dashboardThemes: marca.dashboardThemes || []
  };
}

/** true quando o theme.json já está no formato novo. */
export const ehFormatoNovo = (theme) => Boolean(theme?.brand);

/**
 * Normaliza qualquer um dos dois formatos para a forma que o app consome.
 * Ponto único de compatibilidade durante a migração.
 */
export function normalizarTema(theme) {
  if (!ehFormatoNovo(theme)) return theme;

  return {
    name: theme.name,
    shortName: theme.shortName,
    title: theme.title,
    data: {
      light: expandirSecao(theme.brand, 'light'),
      dark: expandirSecao(theme.brand, 'dark')
    }
  };
}

export default { expandirSecao, normalizarTema, ehFormatoNovo, STATUS_SEMANTIC };
